import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormControl,
  Validators,
  type ValidatorFn,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, forkJoin, merge, of, Subject } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import type { ConfirmationDialogParams } from "../../shared/confirmation-dialog/confirmation-dialog.interface";
import { EmployeesService } from "../../employees/employees.service";
import type {
  Employee,
  EmployeePickerItem,
  CurrentUserResponse,
} from "../../employees/employees.interface";
import { BranchSettingsService } from "../../branch-settings/branch-settings.service";
import type { Branch } from "../../branch-settings/branch-settings.interface";
import { PrintCardService } from "../../print-cards/print-card.service";
import {
  DEFAULT_PRINT_CARD_PLASTIC_PAGE_SIZE_PRESET_ID,
  isPageSizePresetAllowedForTemplate,
  PRINT_CARD_DEFAULT_PAGE_SIZE_PRESET_ID,
  PRINT_CARD_PLASTIC_QUANTITY_MAX,
  PRINT_CARD_PLASTIC_QUANTITY_MIN,
  printCardMaterialForTemplateType,
  type PrintCardPageSizePreset,
  type PrintCardTemplateItem,
  type PrintCardTemplateType,
} from "../../print-cards/print-cards.interface";
import { UpsShopRatesDialogComponent } from "../../print-card-orders/ups-shop-rates-dialog/ups-shop-rates-dialog.component";
import type { UpsWeightUnit } from "../../print-card-orders/print-card-orders.interface";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import {
  CARD_ORDER_CREATE_STEP,
  clampCardOrderCreateStep,
  type CardOrder,
  type CardOrderAddressValidationState,
  type CardOrderCartEmployeeGroup,
  type CardOrderCartLine,
  type CardOrderCreateCheckoutDraft,
  CARD_ORDER_DELIVERY_SPEEDS,
  CARD_ORDER_PRODUCTION_TIMES,
  type CardOrderDeliveryDestination,
  type CardOrderDeliverySpeed,
  type CardOrderDeliveryType,
  type CardOrderProductionTime,
  type CardOrderOptions,
  type CardOrderTestShippingAddress,
  type CardOrderUpsAccessPoint,
  type BranchShippingDetails,
  type DeliverableBranch,
  type OrderItemsListRecipient,
  type PlacingUserContactSource,
} from "../card-orders.interface";
import { CardOrdersUpsService } from "../card-orders-ups.service";
import { CardOrdersService } from "../card-orders.service";
import {
  buildCartLineId,
  cartLinesToRecipientsPayload,
  cartLinesToSelfServiceRecipientsPayload,
  DEFAULT_CARD_ORDER_QUANTITY,
  employeeToPickerItem,
  formatShippingAddressLines,
  formatUpsAccessPointAddress,
  formatUpsAccessPointDistanceKm,
  formatUpsAccessPointName,
  formatUpsAccessPointOpeningHours as localizeUpsAccessPointOpeningHours,
  getCountryDisplayName as getLocalizedCountryDisplayName,
  getCountryFlagUrl,
  getDeliverableBranches,
  getOrderQuantityOptionsForTemplate,
  getTemplateTypeLabelKey,
  getTotalCartQuantity,
  groupCartLinesByEmployeeOrdered,
  isPrinterTemplateType,
  UPS_SANDBOX_TEST_SHIPPING_ADDRESSES,
  getCardOrderShipmentMethods,
  getDeliverySpeedLabelKey,
  getProductionTimeLabelKey,
  getShipmentMethodLabelKey,
  branchShippingDetails,
  getPlacingUserContactDefaults,
} from "../card-order.util";
import {
  clearCardOrderCreateDraft,
  loadCardOrderCreateDraft,
  saveCardOrderCreateDraft,
} from "../card-order-draft.storage";
import { environment } from "../../../environments/environment";
import { validatePhoneNumber } from "../../shared/validators/phone-number.validator";
import { AuthService } from "../../auth/auth.service";
import { CompanyService } from "../../company/company.service";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";

const EMPLOYEE_SEARCH_DEBOUNCE_MS = 280;
const EMPLOYEE_LIST_PAGE_SIZE = 20;
const DRAFT_PERSIST_DEBOUNCE_MS = 350;
const CHECKOUT_FORM_DEBOUNCE_MS = 400;
const EMPTY_CHECKOUT_FORM = {
  line1: "",
  line2: "",
  city: "",
  postalCode: "",
  country: "PL",
  phone: "",
  recipientName: "",
  companyName: "",
  deliveryType: "COURIER" as CardOrderDeliveryType,
  deliverySpeed: "STANDARD" as CardOrderDeliverySpeed,
  productionTime: "STANDARD" as CardOrderProductionTime,
  notes: "",
  accessPointId: "",
};

const requiredValidator: ValidatorFn = (control) =>
  Validators.required(control);

const phoneValidators: ValidatorFn[] = [
  requiredValidator,
  (control: AbstractControl) =>
    validatePhoneNumber(control as UntypedFormControl),
];

@Component({
  selector: "app-card-order-create",
  templateUrl: "./card-order-create.component.html",
  styleUrls: ["./card-order-create.component.scss"],
  standalone: false,
})
export class CardOrderCreateComponent implements OnInit, OnDestroy {
  readonly showTestShippingAddresses = !environment.production;
  readonly testShippingAddresses = UPS_SANDBOX_TEST_SHIPPING_ADDRESSES;
  readonly templateTypeLabelKey = getTemplateTypeLabelKey;
  readonly getCountryFlagUrl = getCountryFlagUrl;
  readonly step = CARD_ORDER_CREATE_STEP;
  readonly isSelfServiceOrder: boolean;
  readonly employeeSearchCtrl = new FormControl<string>("", {
    nonNullable: true,
  });
  readonly selectedEmployeeSearchCtrl = new FormControl<string>("", {
    nonNullable: true,
  });

  stepIndex = 0;
  loading = true;
  employeesLoading = false;
  submitting = false;
  templates: PrintCardTemplateItem[] = [];
  presets: PrintCardPageSizePreset[] = [];
  orderOptions: CardOrderOptions | null = null;
  employeeOptions: EmployeePickerItem[] = [];
  allPickerEmployees: EmployeePickerItem[] = [];
  readonly employeePageSizeOptions = [10, 20, 100];
  employeeListTotal = 0;
  employeePageIndex = 0;
  employeePageSize = EMPLOYEE_LIST_PAGE_SIZE;
  branches: Branch[] = [];
  selectedEmployees: EmployeePickerItem[] = [];
  private readonly selectedBranchIds = new Set<string>();
  private employeeById = new Map<string, EmployeePickerItem>();
  activeEmployee: EmployeePickerItem | null = null;
  selectedTemplate: PrintCardTemplateItem | null = null;
  selectedPageSizePresetId = "";
  selectedQuantity = DEFAULT_CARD_ORDER_QUANTITY;
  cartLines: CardOrderCartLine[] = [];
  cartEmployeeGroups: CardOrderCartEmployeeGroup[] = [];
  employeesWithCartCount = 0;
  private employeeCartCounts = new Map<string, number>();
  previewFrontHtml: string | null = null;
  previewBackHtml: string | null = null;
  previewPageWidthCm: number | null = null;
  previewPageHeightCm: number | null = null;
  previewLoading = false;
  accessPoints: CardOrderUpsAccessPoint[] = [];
  accessPointsLoading = false;
  deliveryDestination: CardOrderDeliveryDestination = "custom";
  selectedDeliveryBranchId: string | null = null;
  checkoutReady = false;
  canSubmitOrder = false;
  canDeliverToBranchView = false;
  isCustomDeliveryView = true;
  showAdminProductionTime = false;
  private productionTimeManuallySet = false;
  deliverableBranchesView: DeliverableBranch[] = [];
  shipmentMethodsView: CardOrderDeliveryType[] = [];
  branchPhoneReadonly = false;
  addressValidationState: CardOrderAddressValidationState = "idle";
  private deliveryDestinationInitialized = false;
  checkoutForm: FormGroup;
  readonly errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  private readonly destroy$ = new Subject<void>();
  private readonly previewTrigger$ = new Subject<{
    templateId: string;
    email: string;
    pageSizePresetId: string;
  } | null>();
  private readonly employeeListReload$ = new Subject<void>();
  private readonly persistDraft$ = new Subject<void>();
  private persistDraftEnabled = false;
  private catalogLoaded = false;
  private catalogLoading = false;
  private catalogLoadCallbacks: Array<() => void> = [];
  private accessPointsRequestSeq = 0;
  private checkoutContactDefaults: {
    recipientName: string;
    phone: string;
    companyName: string;
  } | null = null;

  constructor(
    private readonly ordersService: CardOrdersService,
    private readonly upsService: CardOrdersUpsService,
    private readonly printCardService: PrintCardService,
    private readonly employeesService: EmployeesService,
    private readonly branchSettingsService: BranchSettingsService,
    private readonly companyService: CompanyService,
    private readonly toastr: ToastrTranslatedService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly auth: AuthService,
    fb: FormBuilder,
  ) {
    this.isSelfServiceOrder = !this.auth.hasCompanyManagerAccess;
    this.checkoutForm = fb.group({
      line1: [EMPTY_CHECKOUT_FORM.line1, requiredValidator],
      line2: [EMPTY_CHECKOUT_FORM.line2],
      city: [EMPTY_CHECKOUT_FORM.city, requiredValidator],
      postalCode: [EMPTY_CHECKOUT_FORM.postalCode, requiredValidator],
      country: [EMPTY_CHECKOUT_FORM.country, requiredValidator],
      phone: [EMPTY_CHECKOUT_FORM.phone, phoneValidators],
      recipientName: [EMPTY_CHECKOUT_FORM.recipientName, requiredValidator],
      companyName: [EMPTY_CHECKOUT_FORM.companyName],
      deliveryType: [EMPTY_CHECKOUT_FORM.deliveryType, requiredValidator],
      deliverySpeed: [EMPTY_CHECKOUT_FORM.deliverySpeed, requiredValidator],
      productionTime: [EMPTY_CHECKOUT_FORM.productionTime, requiredValidator],
      notes: [EMPTY_CHECKOUT_FORM.notes],
      accessPointId: [EMPTY_CHECKOUT_FORM.accessPointId],
    });
  }

  ngOnInit(): void {
    this.setupPreviewPipeline();
    if (this.isSelfServiceOrder) {
      this.stepIndex = this.step.Cards;
      this.loadSelfServiceStep();
    } else {
      this.loadEmployeesStep();
      this.setupEmployeeSearch();
    }
    this.setupDraftPersistence();
    this.setupCheckoutFormWatchers();
  }

  getCountryDisplayName(alpha2Code: string | null | undefined): string {
    return getLocalizedCountryDisplayName(
      alpha2Code,
      this.translate.currentLang,
    );
  }

  get showBackButton(): boolean {
    const minStep = this.isSelfServiceOrder
      ? this.step.Cards
      : this.step.Employees;
    return this.stepIndex > minStep;
  }

  get showPrimaryNavButton(): boolean {
    if (this.stepIndex === this.step.Checkout) {
      return this.checkoutReady;
    }
    return true;
  }

  get primaryNavLabelKey(): string {
    if (this.stepIndex === this.step.Checkout) {
      return "cardOrders.reviewOrder";
    }
    if (this.stepIndex === this.step.Summary) {
      return "cardOrders.placeOrder";
    }
    return "cardOrders.next";
  }

  get primaryNavDisabled(): boolean {
    if (this.stepIndex === this.step.Employees) {
      return !this.canProceedFromEmployees;
    }
    if (
      this.stepIndex === this.step.Cards ||
      this.stepIndex === this.step.Cart
    ) {
      return !this.canProceedFromCart;
    }
    if (this.stepIndex === this.step.Summary) {
      return this.submitting;
    }
    return false;
  }

  onPrimaryNav(): void {
    if (this.stepIndex === this.step.Checkout) {
      this.proceedToSummary();
      return;
    }
    if (this.stepIndex === this.step.Summary) {
      this.submitOrder();
      return;
    }
    this.goNext();
  }

  goNext(): void {
    if (!this.canLeaveCurrentStep()) {
      return;
    }
    this.setStepIndex(this.stepIndex + 1);
  }

  goBack(): void {
    const minStep = this.isSelfServiceOrder
      ? this.step.Cards
      : this.step.Employees;
    if (this.stepIndex <= minStep) {
      return;
    }
    this.setStepIndex(this.stepIndex - 1);
  }

  private canLeaveCurrentStep(): boolean {
    if (this.stepIndex === this.step.Employees) {
      return this.canProceedFromEmployees;
    }
    if (
      this.stepIndex === this.step.Cards ||
      this.stepIndex === this.step.Cart
    ) {
      return this.canProceedFromCart;
    }
    if (this.stepIndex === this.step.Checkout) {
      return this.canSubmitOrder;
    }
    return true;
  }

  private setStepIndex(index: number): void {
    const next = clampCardOrderCreateStep(index);
    if (next === this.stepIndex) {
      return;
    }
    if (this.stepIndex === this.step.Cards && next !== this.step.Cards) {
      this.clearPreviewState();
    }
    if (next !== this.step.Checkout && next !== this.step.Summary) {
      this.checkoutReady = false;
    }
    this.stepIndex = next;
    this.scheduleDraftPersist();
    if (next === this.step.Cards) {
      this.ensureActiveEmployeeOnCardsStep();
    }
    if (next === this.step.Checkout) {
      this.openCheckoutStep();
      return;
    }
    if (next === this.step.Summary) {
      this.checkoutReady = true;
      return;
    }
    if (next >= this.step.Cards) {
      this.ensureCatalogLoaded();
    }
  }

  private ensureActiveEmployeeOnCardsStep(): void {
    const first = this.selectedEmployees[0] ?? null;
    const changed =
      !this.activeEmployee ||
      !first ||
      this.emailKey(this.activeEmployee.email) !== this.emailKey(first.email);
    this.activeEmployee = first;
    if (!changed) {
      return;
    }
    this.syncQuantityFromCart();
    this.reloadPreviewIfPossible();
    this.scheduleDraftPersist();
  }

  private openCheckoutStep(): void {
    this.ensureCatalogLoaded(() => {
      this.syncCheckoutState();
      this.applyCheckoutContactDefaults();
      this.refreshAccessPointsWhenAddressReady();
      this.checkoutReady = true;
    });
  }

  private refreshCheckoutViewState(): void {
    this.deliverableBranchesView = getDeliverableBranches(this.branches);
    this.canDeliverToBranchView =
      this.deliverableBranchesView.length > 0 && this.printerEnabled;
    this.isCustomDeliveryView =
      this.checkoutDeliveryType === "PICKUP" ||
      !this.canDeliverToBranchView ||
      this.deliveryDestination === "custom";
    this.shipmentMethodsView = getCardOrderShipmentMethods(this.orderOptions);
    this.branchPhoneReadonly =
      this.checkoutDeliveryType !== "PICKUP" &&
      !this.isCustomDeliveryView &&
      !!this.selectedBranchShipping?.phone;
  }

  private updateCanSubmitOrder(): void {
    if (this.cartLines.length === 0) {
      this.canSubmitOrder = false;
      return;
    }

    const shipping = this.checkoutShipping;
    const phoneControl = this.checkoutForm.get("phone");
    if (
      !shipping.recipientName?.trim() ||
      !shipping.phone?.trim() ||
      phoneControl?.invalid
    ) {
      this.canSubmitOrder = false;
      return;
    }

    if (shipping.deliveryType !== "PICKUP") {
      if (this.isCustomDeliveryView) {
        if (!this.checkoutForm.valid) {
          this.canSubmitOrder = false;
          return;
        }
      } else if (!this.selectedBranchShipping) {
        this.canSubmitOrder = false;
        return;
      }
    } else if (
      !shipping.recipientName?.trim() ||
      !shipping.phone?.trim() ||
      phoneControl?.invalid ||
      !shipping.deliverySpeed
    ) {
      this.canSubmitOrder = false;
      return;
    }

    if (shipping.deliveryType === "ACCESS_POINT") {
      if (!this.upsEnabled || !shipping.accessPointId) {
        this.canSubmitOrder = false;
        return;
      }
    }

    this.canSubmitOrder = true;
  }

  private setupCheckoutFormWatchers(): void {
    this.checkoutForm.valueChanges
      .pipe(debounceTime(CHECKOUT_FORM_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => {
        this.scheduleDraftPersist();
      });

    const deliveryTypeControl = this.checkoutForm.get("deliveryType");
    if (deliveryTypeControl) {
      deliveryTypeControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.resetAddressValidationState();
          this.refreshCheckoutViewState();
          this.updateCheckoutAddressValidators();
          if (this.stepIndex !== this.step.Checkout || !this.checkoutReady) {
            return;
          }
          this.refreshAccessPointsWhenAddressReady();
          this.updateCanSubmitOrder();
        });
    }

    const deliverySpeedControl = this.checkoutForm.get("deliverySpeed");
    if (deliverySpeedControl) {
      deliverySpeedControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((speed: CardOrderDeliverySpeed) => {
          if (this.productionTimeManuallySet || !speed) {
            return;
          }
          this.checkoutForm.patchValue(
            {
              productionTime: speed === "EXPRESS" ? "EXPRESS" : "STANDARD",
            },
            { emitEvent: false },
          );
        });
    }

    const productionTimeControl = this.checkoutForm.get("productionTime");
    if (productionTimeControl) {
      productionTimeControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.showAdminProductionTime) {
            this.productionTimeManuallySet = true;
          }
        });
    }

    const line1Control = this.checkoutForm.get("line1");
    const line2Control = this.checkoutForm.get("line2");
    const cityControl = this.checkoutForm.get("city");
    const postalCodeControl = this.checkoutForm.get("postalCode");
    const countryControl = this.checkoutForm.get("country");
    if (line1Control && cityControl && postalCodeControl && countryControl) {
      merge(
        line1Control.valueChanges,
        line2Control?.valueChanges ?? EMPTY,
        cityControl.valueChanges,
        postalCodeControl.valueChanges,
        countryControl.valueChanges,
      )
        .pipe(debounceTime(CHECKOUT_FORM_DEBOUNCE_MS), takeUntil(this.destroy$))
        .subscribe(() => {
          this.resetAddressValidationState();
          if (this.stepIndex === this.step.Checkout) {
            this.refreshAccessPointsWhenAddressReady();
          }
        });
    }

    this.checkoutForm.statusChanges
      .pipe(debounceTime(50), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.stepIndex === this.step.Checkout && this.checkoutReady) {
          this.updateCanSubmitOrder();
        }
      });
  }

  private setupDraftPersistence(): void {
    this.persistDraft$
      .pipe(debounceTime(DRAFT_PERSIST_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => this.persistDraft());
  }

  scheduleDraftPersist(): void {
    this.persistDraft$.next();
  }

  confirmClearDraft(): void {
    const data: ConfirmationDialogParams = {
      title: "cardOrders.clearDraftTitle",
      body: "cardOrders.clearDraftBody",
      confirmButton: "cardOrders.clearDraft",
    };
    this.dialog
      .open(ConfirmationDialogComponent, {
        width: "400px",
        data,
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.resetOrderDraftState();
        this.toastr.success("cardOrders.clearDraftSuccess");
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.previewTrigger$.complete();
  }

  get printerEnabled(): boolean {
    return this.orderOptions?.enabled ?? false;
  }

  get upsEnabled(): boolean {
    return this.orderOptions?.upsEnabled ?? false;
  }

  get addressValidationLabelKey(): string | null {
    switch (this.addressValidationState) {
      case "validating":
        return "cardOrders.addressValidating";
      case "valid":
        return "cardOrders.addressValidated";
      case "invalid":
        return "cardOrders.addressInvalid";
      default:
        return null;
    }
  }

  get checkoutDeliveryType(): CardOrderDeliveryType {
    return (
      (this.checkoutForm.get("deliveryType")?.value as
        CardOrderDeliveryType | undefined) ?? "COURIER"
    );
  }

  get isPickupDelivery(): boolean {
    return this.checkoutDeliveryType === "PICKUP";
  }

  readonly deliverySpeedOptions = CARD_ORDER_DELIVERY_SPEEDS;
  readonly productionTimeOptions = CARD_ORDER_PRODUCTION_TIMES;
  readonly getDeliverySpeedLabelKey = getDeliverySpeedLabelKey;
  readonly getProductionTimeLabelKey = getProductionTimeLabelKey;

  get isSuperAdmin(): boolean {
    return this.auth.isSuperAdmin;
  }

  readonly sumLineQuantities = getTotalCartQuantity;

  get quantityOptions(): number[] {
    return getOrderQuantityOptionsForTemplate(
      this.selectedTemplate?.type,
      this.printerEnabled,
      this.orderOptions?.quantitiesByTemplateType,
      this.selectedTemplate?.settings?.allowedQuantities,
    );
  }

  get usesFreeformQuantity(): boolean {
    return (
      this.selectedTemplate?.type === "PLASTIC_NFC" &&
      !this.selectedTemplate.settings?.allowedQuantities?.length
    );
  }

  readonly freeformQuantityMin = PRINT_CARD_PLASTIC_QUANTITY_MIN;
  readonly freeformQuantityMax = PRINT_CARD_PLASTIC_QUANTITY_MAX;

  get templatesLoading(): boolean {
    return (
      this.catalogLoading ||
      (!this.catalogLoaded && this.stepIndex >= this.step.Cards)
    );
  }

  get availableTemplates(): PrintCardTemplateItem[] {
    return this.templates.filter(
      (template) =>
        (template.isActive || template.isPreview) &&
        (!this.printerEnabled || isPrinterTemplateType(template.type)),
    );
  }

  get isSelectedTemplatePreviewOnly(): boolean {
    return (
      this.selectedTemplate?.isPreview === true &&
      !this.selectedTemplate.isActive
    );
  }

  get isPlasticNfcTemplateSelected(): boolean {
    return this.selectedTemplate?.type === "PLASTIC_NFC";
  }

  get availablePresets(): PrintCardPageSizePreset[] {
    if (!this.selectedTemplate) {
      return [];
    }
    const material = printCardMaterialForTemplateType(
      this.selectedTemplate.type,
    );
    const supportedIds = this.orderOptions?.supportedPageSizePresetIds ?? [];
    return this.presets.filter((preset) => {
      if (preset.material !== material) {
        return false;
      }
      if (this.printerEnabled && supportedIds.length > 0) {
        return supportedIds.includes(preset.id);
      }
      return true;
    });
  }

  get selectablePresets(): PrintCardPageSizePreset[] {
    return this.availablePresets.filter((preset) =>
      this.isPageSizePresetSelectable(preset.id),
    );
  }

  isPageSizePresetSelectable(presetId: string): boolean {
    return isPageSizePresetAllowedForTemplate(
      presetId,
      this.selectedTemplate?.settings?.allowedPageSizePresetIds,
    );
  }

  get selectedAccessPoint(): CardOrderUpsAccessPoint | null {
    const id = this.checkoutForm.get("accessPointId")?.value as string;
    return this.accessPoints.find((point) => point.id === id) ?? null;
  }

  summaryRecipients: OrderItemsListRecipient[] = [];

  get selectedBranchShipping(): BranchShippingDetails | null {
    const branch = this.getSelectedDeliveryBranch();
    return branch ? branchShippingDetails(branch) : null;
  }

  private getSelectedDeliveryBranch(): Branch | null {
    if (!this.selectedDeliveryBranchId) {
      return null;
    }
    return (
      this.branches.find(
        (item) => item.branchId === this.selectedDeliveryBranchId,
      ) ?? null
    );
  }

  get checkoutShipping(): CardOrderCreateCheckoutDraft {
    const raw = this.checkoutForm.getRawValue() as CardOrderCreateCheckoutDraft;
    if (
      raw.deliveryType !== "PICKUP" &&
      !this.isCustomDeliveryView &&
      this.selectedBranchShipping
    ) {
      const branchShipping = this.selectedBranchShipping;
      return {
        line1: branchShipping.address.line1,
        line2: branchShipping.address.line2 ?? "",
        city: branchShipping.address.city,
        postalCode: branchShipping.address.postalCode,
        country: branchShipping.address.country,
        phone: branchShipping.phone ?? raw.phone,
        recipientName: raw.recipientName,
        companyName: raw.companyName,
        deliveryType: raw.deliveryType,
        deliverySpeed: raw.deliverySpeed,
        productionTime: raw.productionTime,
        notes: raw.notes,
        accessPointId: raw.accessPointId,
      };
    }
    return raw;
  }

  get canProceedFromEmployees(): boolean {
    return this.selectedEmployees.length > 0;
  }

  get allEmployeesSelected(): boolean {
    return (
      this.allPickerEmployees.length > 0 &&
      this.selectedEmployees.length === this.allPickerEmployees.length
    );
  }

  get canProceedFromCart(): boolean {
    return this.cartLines.length > 0;
  }

  get hasSelectedTemplate(): boolean {
    return Boolean(this.selectedTemplate);
  }

  get currentCartLine(): CardOrderCartLine | null {
    if (
      !this.activeEmployee ||
      !this.selectedTemplate ||
      !this.selectedPageSizePresetId
    ) {
      return null;
    }
    const lineId = buildCartLineId(
      this.activeEmployee.email,
      this.selectedTemplate.printCardTemplateId,
      this.selectedPageSizePresetId,
    );
    return this.cartLines.find((line) => line.id === lineId) ?? null;
  }

  get activeEmployeeCartLines(): CardOrderCartLine[] {
    const employee = this.activeEmployee;
    if (!employee) {
      return [];
    }
    return this.cartLines.filter(
      (line) => line.employee.email === employee.email,
    );
  }

  get totalCartQuantity(): number {
    return getTotalCartQuantity(this.cartLines);
  }

  get canBulkAddToCart(): boolean {
    return (
      !!this.selectedTemplate &&
      !this.isSelectedTemplatePreviewOnly &&
      !!this.selectedPageSizePresetId &&
      this.selectedEmployees.length > 0
    );
  }

  get filteredSelectedEmployees(): EmployeePickerItem[] {
    const phrase = this.selectedEmployeeSearchCtrl.value.trim().toLowerCase();
    if (!phrase) {
      return this.selectedEmployees;
    }
    return this.selectedEmployees.filter((employee) => {
      const label = this.getEmployeeLabel(employee).toLowerCase();
      return (
        label.includes(phrase) || employee.email.toLowerCase().includes(phrase)
      );
    });
  }

  getEmployeeCartCount(email: string): number {
    return this.employeeCartCounts.get(this.emailKey(email)) ?? 0;
  }

  hasEmployeeCartItems(email: string): boolean {
    return this.getEmployeeCartCount(email) > 0;
  }

  loadEmployeesStep(): void {
    this.loading = true;
    this.ensureCatalogLoaded();
    forkJoin({
      allEmployees: this.employeesService.getNotExcludedEmployeesWithAliases(),
      branches: this.branchSettingsService.fetchBranches(),
      currentUser: this.employeesService
        .getCurrentUser()
        .pipe(catchError(() => of(null))),
      company: this.companyService.get().pipe(catchError(() => of(null))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ allEmployees, branches, currentUser, company }) => {
          this.branches = (branches ?? []).map((branch) => ({
            ...branch,
            employeeIds: branch.employeeIds ?? [],
          }));
          this.buildEmployeeDirectory(
            Array.isArray(allEmployees) ? allEmployees : [],
          );
          this.refreshCheckoutViewState();
          this.restoreDraftIfPresent();
          this.setCheckoutContactDefaults(currentUser, company?.name);
          this.applyCheckoutContactDefaults();
          this.persistDraftEnabled = true;
          this.employeeListReload$.next();
          this.loading = false;
          if (this.stepIndex === this.step.Checkout) {
            this.openCheckoutStep();
          }
        },
        error: () => {
          this.loading = false;
          this.toastr.error("cardOrders.loadFailed");
        },
      });
  }

  loadSelfServiceStep(): void {
    this.loading = true;
    this.employeesService
      .getCurrentUser()
      .pipe(
        switchMap((user) => {
          const employee = this.buildSelfServiceEmployee(user);
          if (!employee) {
            throw new Error("Missing employeeId for current user");
          }
          this.initializeSelfServiceEmployee(employee);
          this.ensureCatalogLoaded();
          return forkJoin({
            branches: this.branchSettingsService.fetchCurrentBranch().pipe(
              map((branch) => [branch]),
              catchError(() => of<Branch[]>([])),
            ),
            company: this.companyService.get().pipe(catchError(() => of(null))),
          }).pipe(
            map(({ branches, company }) => ({
              user,
              branches,
              company,
            })),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ user, branches, company }) => {
          this.branches = (branches ?? []).map((branch) => ({
            ...branch,
            employeeIds: branch.employeeIds ?? [],
          }));
          this.refreshCheckoutViewState();
          this.restoreDraftIfPresent();
          this.setCheckoutContactDefaults(user, company?.name);
          this.applyCheckoutContactDefaults();
          this.persistDraftEnabled = true;
          this.loading = false;
          if (this.stepIndex === this.step.Checkout) {
            this.openCheckoutStep();
          }
        },
        error: () => {
          this.loading = false;
          this.toastr.error("cardOrders.loadFailed");
        },
      });
  }

  private buildSelfServiceEmployee(
    user: CurrentUserResponse,
  ): EmployeePickerItem | null {
    const employeeId = user.employeeId?.trim();
    if (!employeeId) {
      return null;
    }
    return {
      employeeId,
      email: user.email.trim(),
      fullName: user.fullName?.trim() || user.name?.trim() || user.email,
      givenName: user.givenName,
      familyName: user.familyName,
    };
  }

  private initializeSelfServiceEmployee(employee: EmployeePickerItem): void {
    this.selectedEmployees = [employee];
    this.activeEmployee = employee;
    this.allPickerEmployees = [employee];
    this.employeeById = new Map([[employee.employeeId, employee]]);
  }

  private ensureCatalogLoaded(onLoaded?: () => void): void {
    if (onLoaded) {
      this.catalogLoadCallbacks.push(onLoaded);
    }
    if (this.catalogLoaded) {
      this.flushCatalogLoadCallbacks();
      return;
    }
    if (this.catalogLoading) {
      return;
    }

    this.catalogLoading = true;
    forkJoin({
      templates: this.printCardService.getTemplates(),
      presets: this.printCardService.getPageSizePresets(),
      options: this.ordersService.getOrderOptions(),
    })
      .pipe(
        finalize(() => {
          this.catalogLoading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ templates, presets, options }) => {
          this.templates = templates?.templates ?? [];
          this.presets = presets?.presets ?? [];
          this.orderOptions = options ?? null;
          this.catalogLoaded = true;
          this.restoreDraftCatalogState();
          this.sanitizeSelectedTemplate();
          this.rebuildCartState();
          this.refreshCheckoutViewState();
          this.flushCatalogLoadCallbacks();
        },
        error: () => {
          const callbacks = this.catalogLoadCallbacks;
          this.catalogLoadCallbacks = [];
          for (const callback of callbacks) {
            callback();
          }
          this.toastr.error("cardOrders.loadFailed");
        },
      });
  }

  private flushCatalogLoadCallbacks(): void {
    if (this.catalogLoadCallbacks.length === 0) {
      return;
    }
    const callbacks = this.catalogLoadCallbacks;
    this.catalogLoadCallbacks = [];
    for (const callback of callbacks) {
      callback();
    }
  }

  private clearPreviewState(): void {
    this.previewLoading = false;
    this.previewFrontHtml = null;
    this.previewBackHtml = null;
    this.previewPageWidthCm = null;
    this.previewPageHeightCm = null;
    this.previewTrigger$.next(null);
  }

  private setupPreviewPipeline(): void {
    this.previewTrigger$
      .pipe(
        switchMap((request) => {
          if (!request) {
            return of(null);
          }
          return this.printCardService
            .preview(
              request.templateId,
              request.email,
              request.pageSizePresetId,
            )
            .pipe(
              catchError(() => {
                this.toastr.error("cardOrders.previewFailed");
                return of(null);
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          if (!result) {
            this.previewFrontHtml = null;
            this.previewBackHtml = null;
            this.previewPageWidthCm = null;
            this.previewPageHeightCm = null;
            this.previewLoading = false;
            return;
          }
          const hasDims =
            Number.isFinite(result.pageWidthCm) &&
            Number.isFinite(result.pageHeightCm) &&
            result.pageWidthCm > 0 &&
            result.pageHeightCm > 0;
          const layout = hasDims
            ? {
                pageWidthCm: result.pageWidthCm,
                pageHeightCm: result.pageHeightCm,
              }
            : this.layoutFromSelection();
          const front = result.frontDocumentHtml.trim();
          this.previewPageWidthCm = layout?.pageWidthCm ?? null;
          this.previewPageHeightCm = layout?.pageHeightCm ?? null;
          this.previewFrontHtml = front.length ? front : null;
          this.previewBackHtml = result.backDocumentHtml.trim().length
            ? result.backDocumentHtml
            : null;
          this.previewLoading = false;
        },
        error: () => {
          this.previewFrontHtml = null;
          this.previewBackHtml = null;
          this.previewPageWidthCm = null;
          this.previewPageHeightCm = null;
          this.previewLoading = false;
        },
      });
  }

  private sanitizeSelectedTemplate(): void {
    if (!this.selectedTemplate) {
      return;
    }

    const selectedTemplate = this.selectedTemplate;
    const isAvailable = this.availableTemplates.some(
      (template) =>
        template.printCardTemplateId === selectedTemplate.printCardTemplateId,
    );
    if (isAvailable) {
      return;
    }

    this.selectedTemplate = null;
    this.selectedPageSizePresetId = "";
    this.clearPreviewState();
  }

  private syncCheckoutState(): void {
    this.refreshCheckoutViewState();
    this.syncDeliveryTypeWithShipmentMethods();
    this.initDeliveryDestination();
    this.updateCheckoutAddressValidators();
    this.updateCanSubmitOrder();
  }

  private onCheckoutDeliveryChanged(): void {
    this.syncCheckoutState();
    this.refreshAccessPointsWhenAddressReady();
    this.scheduleDraftPersist();
  }

  private resetAddressValidationState(): void {
    if (this.addressValidationState !== "idle") {
      this.addressValidationState = "idle";
    }
  }

  private validateAddressWithUps(): { ok: boolean; errorKey?: string } {
    if (!this.upsEnabled) {
      return { ok: true };
    }
    this.addressValidationState = "validating";
    const shipping = this.checkoutShipping;
    const result = this.upsService.validateAddress({
      line1: shipping.line1,
      line2: shipping.line2 || undefined,
      city: shipping.city,
      postalCode: shipping.postalCode,
      country: shipping.country,
    });
    this.addressValidationState = result.ok ? "valid" : "invalid";
    return result;
  }

  private emailKey(email: string): string {
    return email.trim().toLowerCase();
  }

  isBranchSelected(branchId: string): boolean {
    return this.selectedBranchIds.has(branchId);
  }

  toggleSelectAll(): void {
    if (this.allEmployeesSelected) {
      this.selectedBranchIds.clear();
      this.setSelectedEmployees([]);
      return;
    }
    this.setSelectedEmployees([...this.allPickerEmployees]);
    this.syncBranchChips();
  }

  toggleBranch(branchId: string): void {
    const branch = this.branches.find((item) => item.branchId === branchId);
    if (!branch) {
      return;
    }

    if (this.selectedBranchIds.has(branchId)) {
      this.selectedBranchIds.delete(branchId);
      const keepEmployeeIds = new Set(
        [...this.selectedBranchIds].flatMap((id) => {
          const selectedBranch = this.branches.find(
            (item) => item.branchId === id,
          );
          return selectedBranch?.employeeIds ?? [];
        }),
      );
      const branchEmployeeIds = new Set(branch.employeeIds);
      this.setSelectedEmployees(
        this.selectedEmployees.filter(
          (employee) =>
            !branchEmployeeIds.has(employee.employeeId) ||
            keepEmployeeIds.has(employee.employeeId),
        ),
      );
      return;
    }

    this.selectedBranchIds.add(branchId);
    this.mergeSelectedEmployees(this.getBranchEmployees(branch));
  }

  private buildEmployeeDirectory(employees: Employee[]): void {
    this.allPickerEmployees = employees
      .map((employee) => employeeToPickerItem(employee))
      .filter((employee): employee is EmployeePickerItem => !!employee);
    this.employeeById = new Map(
      this.allPickerEmployees.map((employee) => [
        employee.employeeId,
        employee,
      ]),
    );
  }

  private pickEmployee(employee: EmployeePickerItem): EmployeePickerItem {
    return (
      this.employeeById.get(employee.employeeId) ??
      this.allPickerEmployees.find((item) => item.email === employee.email) ??
      employee
    );
  }

  private restoreDraftIfPresent(): void {
    const draft = loadCardOrderCreateDraft();
    if (!draft) {
      return;
    }

    this.stepIndex = this.isSelfServiceOrder
      ? Math.max(draft.stepIndex, this.step.Cards)
      : draft.stepIndex;
    this.selectedBranchIds.clear();
    for (const branchId of draft.selectedBranchIds) {
      this.selectedBranchIds.add(branchId);
    }
    this.selectedEmployees = draft.selectedEmployees.map((employee) =>
      this.pickEmployee(employee),
    );

    if (this.isSelfServiceOrder && this.activeEmployee) {
      this.initializeSelfServiceEmployee(this.activeEmployee);
    }

    const selectedEmails = new Set(
      this.selectedEmployees.map((employee) => this.emailKey(employee.email)),
    );
    this.cartLines = draft.cartLines
      .map((line) => ({
        ...line,
        employee: this.pickEmployee(line.employee),
      }))
      .filter((line) => selectedEmails.has(this.emailKey(line.employee.email)));
    this.rebuildCartState();

    this.activeEmployee = draft.activeEmployeeEmail
      ? (this.selectedEmployees.find(
          (employee) => employee.email === draft.activeEmployeeEmail,
        ) ?? null)
      : (this.selectedEmployees[0] ?? null);

    this.syncBranchChips();
    const checkoutDraft = draft.checkoutForm;
    this.checkoutForm.patchValue(
      {
        ...checkoutDraft,
        productionTime:
          checkoutDraft.productionTime ??
          (checkoutDraft.deliverySpeed === "EXPRESS" ? "EXPRESS" : "STANDARD"),
      },
      { emitEvent: false },
    );

    if (draft.deliveryDestination) {
      this.deliveryDestination = draft.deliveryDestination;
      this.deliveryDestinationInitialized = true;
    }
    if (draft.selectedDeliveryBranchId) {
      this.selectedDeliveryBranchId = draft.selectedDeliveryBranchId;
    }
  }

  private restoreDraftCatalogState(): void {
    const draft = loadCardOrderCreateDraft();
    if (!draft) {
      return;
    }

    this.selectedTemplate =
      this.templates.find(
        (template) => template.printCardTemplateId === draft.selectedTemplateId,
      ) ?? null;
    const presets = this.selectablePresets;
    this.selectedPageSizePresetId = presets.some(
      (preset) => preset.id === draft.selectedPageSizePresetId,
    )
      ? draft.selectedPageSizePresetId
      : (presets[0]?.id ?? "");
    this.selectedQuantity = draft.selectedQuantity;
    this.syncQuantityFromCart();

    if (
      this.stepIndex === this.step.Cards &&
      this.selectedTemplate &&
      this.activeEmployee
    ) {
      this.loadPreview();
    }
  }

  private persistDraft(): void {
    if (!this.persistDraftEnabled || this.loading) {
      return;
    }
    if (
      this.selectedEmployees.length === 0 &&
      this.cartLines.length === 0 &&
      this.stepIndex === this.step.Employees
    ) {
      clearCardOrderCreateDraft();
      return;
    }

    const checkoutValue =
      this.checkoutForm.getRawValue() as CardOrderCreateCheckoutDraft;
    saveCardOrderCreateDraft({
      version: 1,
      stepIndex: this.stepIndex,
      selectedEmployees: this.selectedEmployees,
      selectedBranchIds: [...this.selectedBranchIds],
      activeEmployeeEmail: this.activeEmployee?.email ?? null,
      selectedTemplateId: this.selectedTemplate?.printCardTemplateId ?? null,
      selectedPageSizePresetId: this.selectedPageSizePresetId,
      selectedQuantity: this.selectedQuantity,
      cartLines: this.cartLines,
      checkoutForm: checkoutValue,
      deliveryDestination: this.deliveryDestination,
      selectedDeliveryBranchId: this.selectedDeliveryBranchId,
    });
  }

  private resetOrderDraftState(): void {
    this.checkoutReady = false;
    this.canSubmitOrder = false;
    this.persistDraftEnabled = false;
    clearCardOrderCreateDraft();
    this.stepIndex = 0;
    this.selectedEmployees = [];
    this.selectedBranchIds.clear();
    this.activeEmployee = null;
    this.selectedTemplate = null;
    this.selectedPageSizePresetId = "";
    this.selectedQuantity = DEFAULT_CARD_ORDER_QUANTITY;
    this.clearPreviewState();
    this.accessPoints = [];
    this.deliveryDestination = "custom";
    this.selectedDeliveryBranchId = null;
    this.deliveryDestinationInitialized = false;
    this.employeeSearchCtrl.setValue("", { emitEvent: false });
    this.selectedEmployeeSearchCtrl.setValue("", { emitEvent: false });
    this.checkoutForm.reset(EMPTY_CHECKOUT_FORM, { emitEvent: false });
    this.applyCheckoutContactDefaults();
    this.cartLines = [];
    this.rebuildCartState();
    this.syncQuantityFromCart();
    this.syncCheckoutState();
    this.persistDraftEnabled = true;
  }

  setDeliveryDestination(destination: CardOrderDeliveryDestination): void {
    this.deliveryDestination = destination;
    this.resetAddressValidationState();
    if (destination === "branch") {
      this.syncSelectedDeliveryBranch();
    }
    this.onCheckoutDeliveryChanged();
  }

  setDeliveryBranch(branchId: string): void {
    this.selectedDeliveryBranchId = branchId;
    this.resetAddressValidationState();
    this.onCheckoutDeliveryChanged();
  }

  onCheckoutAddressFieldBlur(): void {
    if (!this.upsEnabled) {
      return;
    }
    this.validateAddressWithUps();
  }

  applyTestShippingAddress(address: CardOrderTestShippingAddress): void {
    if (this.deliveryDestination !== "custom") {
      this.setDeliveryDestination("custom");
    }
    this.checkoutForm.patchValue({
      line1: address.line1,
      line2: address.line2 ?? "",
      city: address.city,
      postalCode: address.postalCode,
      country: address.country,
    });
    this.onCheckoutDeliveryChanged();
  }

  private applyBranchPhoneToForm(): void {
    const phone = this.selectedBranchShipping?.phone;
    if (!this.isCustomDeliveryView && phone) {
      this.checkoutForm.patchValue({ phone }, { emitEvent: false });
    }
  }

  private initDeliveryDestination(): void {
    if (!this.canDeliverToBranchView) {
      this.deliveryDestination = "custom";
      this.selectedDeliveryBranchId = null;
      return;
    }

    if (!this.deliveryDestinationInitialized) {
      this.deliveryDestinationInitialized = true;
      if (this.deliveryDestination !== "custom") {
        this.deliveryDestination = "branch";
      }
    }

    this.syncSelectedDeliveryBranch();
    this.applyBranchPhoneToForm();
  }

  private syncSelectedDeliveryBranch(): void {
    if (!this.canDeliverToBranchView || this.deliveryDestination !== "branch") {
      return;
    }

    const hasValidSelection =
      !!this.selectedDeliveryBranchId &&
      this.deliverableBranchesView.some(
        (item) => item.branch.branchId === this.selectedDeliveryBranchId,
      );

    if (!hasValidSelection) {
      this.selectedDeliveryBranchId =
        this.deliverableBranchesView[0]?.branch.branchId ?? null;
    }
  }

  private updateCheckoutAddressValidators(): void {
    const requireAddress = !this.isPickupDelivery && this.isCustomDeliveryView;
    const addressFields = ["line1", "city", "postalCode", "country"] as const;
    for (const fieldName of addressFields) {
      const control = this.checkoutForm.get(fieldName);
      if (requireAddress) {
        control?.setValidators(requiredValidator);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity({ emitEvent: false });
    }

    const phoneControl = this.checkoutForm.get("phone");
    phoneControl?.setValidators(phoneValidators);
    phoneControl?.updateValueAndValidity({ emitEvent: false });

    const recipientNameControl = this.checkoutForm.get("recipientName");
    recipientNameControl?.setValidators(requiredValidator);
    recipientNameControl?.updateValueAndValidity({ emitEvent: false });

    const deliverySpeedControl = this.checkoutForm.get("deliverySpeed");
    deliverySpeedControl?.setValidators(requiredValidator);
    deliverySpeedControl?.updateValueAndValidity({ emitEvent: false });

    this.checkoutForm.updateValueAndValidity({ emitEvent: false });
  }

  private setCheckoutContactDefaults(
    user: CurrentUserResponse | PlacingUserContactSource | null,
    companyName?: string | null,
  ): void {
    const contact = user ? getPlacingUserContactDefaults(user) : null;
    this.checkoutContactDefaults = {
      recipientName: contact?.recipientName?.trim() || "",
      phone: contact?.phone?.trim() || "",
      companyName: companyName?.trim() || "",
    };
  }

  private applyCheckoutContactDefaults(): void {
    const defaults = this.checkoutContactDefaults;
    if (!defaults) {
      return;
    }

    const current =
      this.checkoutForm.getRawValue() as CardOrderCreateCheckoutDraft;
    const patch: Partial<CardOrderCreateCheckoutDraft> = {};

    if (!current.recipientName?.trim() && defaults.recipientName) {
      patch.recipientName = defaults.recipientName;
    }

    const shouldApplyPhone =
      !current.phone?.trim() &&
      !!defaults.phone &&
      (this.isCustomDeliveryView || !this.selectedBranchShipping?.phone);

    if (shouldApplyPhone) {
      patch.phone = defaults.phone;
    }

    if (!current.companyName?.trim() && defaults.companyName) {
      patch.companyName = defaults.companyName;
    }

    if (Object.keys(patch).length === 0) {
      return;
    }

    this.checkoutForm.patchValue(patch, { emitEvent: false });
    this.updateCanSubmitOrder();
  }

  private getBranchEmployees(branch: Branch): EmployeePickerItem[] {
    const employees: EmployeePickerItem[] = [];
    for (const employeeId of branch.employeeIds) {
      const employee = this.employeeById.get(employeeId);
      if (employee) {
        employees.push(employee);
      }
    }
    return employees;
  }

  private mergeSelectedEmployees(incoming: EmployeePickerItem[]): void {
    const byEmail = new Map(
      this.selectedEmployees.map((employee) => [
        this.emailKey(employee.email),
        employee,
      ]),
    );
    for (const employee of incoming) {
      byEmail.set(this.emailKey(employee.email), employee);
    }
    this.setSelectedEmployees([...byEmail.values()]);
  }

  private setSelectedEmployees(employees: EmployeePickerItem[]): void {
    const selectedEmails = new Set(
      employees.map((employee) => this.emailKey(employee.email)),
    );
    this.selectedEmployees = employees;
    if (
      this.activeEmployee &&
      !selectedEmails.has(this.emailKey(this.activeEmployee.email))
    ) {
      this.activeEmployee = employees[0] ?? null;
      this.reloadPreviewIfPossible();
    }
    this.replaceCartLines(
      this.cartLines.filter((line) =>
        selectedEmails.has(this.emailKey(line.employee.email)),
      ),
    );
    this.syncBranchChips();
    this.scheduleDraftPersist();
  }

  private isBranchFullySelected(branchId: string): boolean {
    const branch = this.branches.find((item) => item.branchId === branchId);
    if (!branch || branch.employeeIds.length === 0) {
      return false;
    }
    const selectedIds = new Set(
      this.selectedEmployees.map((employee) => employee.employeeId),
    );
    return branch.employeeIds.every((employeeId) =>
      selectedIds.has(employeeId),
    );
  }

  private syncBranchChips(): void {
    for (const branchId of [...this.selectedBranchIds]) {
      if (!this.isBranchFullySelected(branchId)) {
        this.selectedBranchIds.delete(branchId);
      }
    }
    for (const branch of this.branches) {
      if (
        branch.employeeIds.length > 0 &&
        !this.selectedBranchIds.has(branch.branchId) &&
        this.isBranchFullySelected(branch.branchId)
      ) {
        this.selectedBranchIds.add(branch.branchId);
      }
    }
  }

  private setupEmployeeSearch(): void {
    this.employeeSearchCtrl.valueChanges
      .pipe(
        debounceTime(EMPLOYEE_SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.employeePageIndex = 0;
        this.employeeListReload$.next();
      });

    this.employeeListReload$
      .pipe(
        switchMap(() => {
          this.employeesLoading = true;
          const phrase = this.employeeSearchCtrl.value.trim();
          return this.employeesService
            .getEmployeesPicker({
              phrase: phrase || undefined,
              pager: {
                page: this.employeePageIndex,
                limit: this.employeePageSize,
              },
            })
            .pipe(
              catchError(() => {
                this.employeesLoading = false;
                this.employeeOptions = [];
                this.employeeListTotal = 0;
                return EMPTY;
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.employeeOptions = response.data;
          this.employeeListTotal = response.total;
          this.employeesLoading = false;
        },
        error: () => {
          this.employeesLoading = false;
        },
      });
  }

  changeEmployeePage(event: PageEvent): void {
    this.employeePageIndex = event.pageIndex;
    this.employeePageSize = event.pageSize;
    this.employeeListReload$.next();
  }

  isEmployeeSelected(employee: EmployeePickerItem): boolean {
    return this.selectedEmployees.some((item) => item.email === employee.email);
  }

  toggleEmployee(employee: EmployeePickerItem): void {
    if (this.isEmployeeSelected(employee)) {
      this.setSelectedEmployees(
        this.selectedEmployees.filter((item) => item.email !== employee.email),
      );
      return;
    }
    this.mergeSelectedEmployees([employee]);
    if (!this.activeEmployee) {
      this.activeEmployee = employee;
      this.reloadPreviewIfPossible();
    }
  }

  selectActiveEmployee(employee: EmployeePickerItem): void {
    if (this.activeEmployee?.email === employee.email) {
      return;
    }
    this.activeEmployee = employee;
    this.syncQuantityFromCart();
    this.reloadPreviewIfPossible();
    this.scheduleDraftPersist();
  }

  selectTemplate(template: PrintCardTemplateItem): void {
    this.selectedTemplate = template;
    const defaultId = this.defaultPageSizePresetId(template.type);
    const presets = this.selectablePresets;
    this.selectedPageSizePresetId =
      presets.find((preset) => preset.id === defaultId)?.id ??
      presets[0]?.id ??
      "";
    this.syncQuantityFromCart();
    this.loadPreview();
    this.scheduleDraftPersist();
  }

  onQuantityChange(quantity: number): void {
    const parsed = Math.floor(Number(quantity));
    if (this.usesFreeformQuantity) {
      this.selectedQuantity = Number.isFinite(parsed)
        ? Math.min(
            PRINT_CARD_PLASTIC_QUANTITY_MAX,
            Math.max(PRINT_CARD_PLASTIC_QUANTITY_MIN, parsed),
          )
        : PRINT_CARD_PLASTIC_QUANTITY_MIN;
      return;
    }
    const options = this.quantityOptions;
    if (options.includes(parsed)) {
      this.selectedQuantity = parsed;
      return;
    }
    this.selectedQuantity = options.includes(DEFAULT_CARD_ORDER_QUANTITY)
      ? DEFAULT_CARD_ORDER_QUANTITY
      : (options[0] ?? DEFAULT_CARD_ORDER_QUANTITY);
  }

  getCartLineQuantityOptions(line: CardOrderCartLine): number[] {
    const template = this.templates.find(
      (item) => item.printCardTemplateId === line.templateId,
    );
    return getOrderQuantityOptionsForTemplate(
      template?.type,
      this.printerEnabled,
      this.orderOptions?.quantitiesByTemplateType,
      template?.settings?.allowedQuantities,
    );
  }

  isCartLineFreeformQuantity(line: CardOrderCartLine): boolean {
    const template = this.templates.find(
      (item) => item.printCardTemplateId === line.templateId,
    );
    return (
      template?.type === "PLASTIC_NFC" &&
      !template.settings?.allowedQuantities?.length
    );
  }

  updateCartQuantity(lineId: string, quantity: number): void {
    const line = this.cartLines.find((item) => item.id === lineId);
    if (!line) {
      return;
    }
    const options = this.getCartLineQuantityOptions(line);
    const freeform = this.isCartLineFreeformQuantity(line);
    const parsed = Math.floor(Number(quantity));
    let nextQuantity = DEFAULT_CARD_ORDER_QUANTITY;
    if (freeform) {
      nextQuantity = Number.isFinite(parsed)
        ? Math.min(
            PRINT_CARD_PLASTIC_QUANTITY_MAX,
            Math.max(PRINT_CARD_PLASTIC_QUANTITY_MIN, parsed),
          )
        : PRINT_CARD_PLASTIC_QUANTITY_MIN;
    } else if (options.includes(parsed)) {
      nextQuantity = parsed;
    } else if (options.includes(DEFAULT_CARD_ORDER_QUANTITY)) {
      nextQuantity = DEFAULT_CARD_ORDER_QUANTITY;
    } else {
      nextQuantity = options[0] ?? DEFAULT_CARD_ORDER_QUANTITY;
    }
    this.replaceCartLines(
      this.cartLines.map((item) =>
        item.id === lineId ? { ...item, quantity: nextQuantity } : item,
      ),
    );
  }

  onPageSizeChange(presetId: string): void {
    if (!this.isPageSizePresetSelectable(presetId)) {
      return;
    }
    this.selectedPageSizePresetId = presetId;
    this.syncQuantityFromCart();
    this.loadPreview();
    this.scheduleDraftPersist();
  }

  isTemplateInActiveEmployeeCart(templateId: string): boolean {
    const employee = this.activeEmployee;
    if (!employee) {
      return false;
    }
    return this.cartLines.some(
      (line) =>
        line.employee.email === employee.email &&
        line.templateId === templateId,
    );
  }

  private syncQuantityFromCart(): void {
    const quantity = this.currentCartLine?.quantity ?? this.selectedQuantity;
    const parsed = Math.floor(Number(quantity));
    if (this.usesFreeformQuantity) {
      this.selectedQuantity = Number.isFinite(parsed)
        ? Math.min(
            PRINT_CARD_PLASTIC_QUANTITY_MAX,
            Math.max(PRINT_CARD_PLASTIC_QUANTITY_MIN, parsed),
          )
        : PRINT_CARD_PLASTIC_QUANTITY_MIN;
      return;
    }
    const options = this.quantityOptions;
    if (options.includes(parsed)) {
      this.selectedQuantity = parsed;
      return;
    }
    this.selectedQuantity = options.includes(DEFAULT_CARD_ORDER_QUANTITY)
      ? DEFAULT_CARD_ORDER_QUANTITY
      : (options[0] ?? DEFAULT_CARD_ORDER_QUANTITY);
  }

  private syncDeliveryTypeWithShipmentMethods(): void {
    const methods = this.shipmentMethodsView;
    if (methods.length === 0) {
      return;
    }
    const control = this.checkoutForm.get("deliveryType");
    const current = control?.value as CardOrderDeliveryType | undefined;
    if (current && methods.includes(current)) {
      return;
    }
    const nextType = methods[0];
    this.checkoutForm.patchValue(
      {
        deliveryType: nextType,
        accessPointId: "",
      },
      { emitEvent: false },
    );
    if (nextType !== "ACCESS_POINT") {
      this.accessPoints = [];
    }
  }

  private layoutFromSelection(): {
    pageWidthCm: number;
    pageHeightCm: number;
  } | null {
    const preset = this.presets.find(
      (item) => item.id === this.selectedPageSizePresetId,
    );
    if (!preset) {
      return null;
    }
    const portrait =
      this.selectedTemplate?.settings?.orientation === "portrait" ||
      this.selectedTemplate?.settings?.designDocument?.orientation ===
        "portrait";
    return {
      pageWidthCm: portrait ? preset.heightCm : preset.widthCm,
      pageHeightCm: portrait ? preset.widthCm : preset.heightCm,
    };
  }

  private reloadPreviewIfPossible(): void {
    if (
      this.stepIndex === this.step.Cards &&
      this.activeEmployee &&
      this.selectedTemplate
    ) {
      this.loadPreview();
      return;
    }
    this.clearPreviewState();
  }

  loadPreview(): void {
    if (
      this.stepIndex !== this.step.Cards ||
      !this.activeEmployee ||
      !this.selectedTemplate ||
      !this.selectedPageSizePresetId
    ) {
      this.clearPreviewState();
      return;
    }
    this.previewLoading = true;
    this.previewFrontHtml = null;
    this.previewBackHtml = null;
    this.previewPageWidthCm = null;
    this.previewPageHeightCm = null;
    this.previewTrigger$.next({
      templateId: this.selectedTemplate.printCardTemplateId,
      email: this.activeEmployee.email,
      pageSizePresetId: this.selectedPageSizePresetId,
    });
  }

  addToCart(): void {
    if (!this.activeEmployee) {
      return;
    }
    if (!this.upsertCartLinesForEmployees([this.activeEmployee])) {
      return;
    }
    this.toastr.success("cardOrders.addedToCartForEmployee", {
      name: this.getEmployeeLabel(this.activeEmployee),
    });
  }

  addToCartForAllEmployees(): void {
    if (!this.canBulkAddToCart) {
      return;
    }
    const count = this.selectedEmployees.length;
    if (!this.upsertCartLinesForEmployees(this.selectedEmployees)) {
      return;
    }
    this.toastr.success("cardOrders.addedToCartForAllEmployees", { count });
  }

  private upsertCartLinesForEmployees(
    employees: EmployeePickerItem[],
  ): boolean {
    if (
      !this.selectedTemplate ||
      !this.selectedPageSizePresetId ||
      this.isSelectedTemplatePreviewOnly
    ) {
      return false;
    }
    if (!this.usesFreeformQuantity && this.quantityOptions.length === 0) {
      return false;
    }
    if (employees.length === 0) {
      return false;
    }
    const quantity = this.usesFreeformQuantity
      ? Math.min(
          PRINT_CARD_PLASTIC_QUANTITY_MAX,
          Math.max(
            PRINT_CARD_PLASTIC_QUANTITY_MIN,
            Math.floor(Number(this.selectedQuantity)) ||
              PRINT_CARD_PLASTIC_QUANTITY_MIN,
          ),
        )
      : this.quantityOptions.includes(this.selectedQuantity)
        ? this.selectedQuantity
        : (this.quantityOptions[0] ?? DEFAULT_CARD_ORDER_QUANTITY);

    const lineIndex = new Map(
      this.cartLines.map((line, index) => [line.id, index] as const),
    );
    const nextLines = [...this.cartLines];

    for (const employee of employees) {
      const lineId = buildCartLineId(
        employee.email,
        this.selectedTemplate.printCardTemplateId,
        this.selectedPageSizePresetId,
      );
      const existingIndex = lineIndex.get(lineId);
      if (existingIndex !== undefined) {
        nextLines[existingIndex] = {
          ...nextLines[existingIndex],
          quantity,
        };
        continue;
      }

      lineIndex.set(lineId, nextLines.length);
      nextLines.push({
        id: lineId,
        employee,
        templateId: this.selectedTemplate.printCardTemplateId,
        templateName: this.selectedTemplate.name,
        pageSizePresetId: this.selectedPageSizePresetId,
        quantity,
      });
    }

    this.replaceCartLines(nextLines);
    return true;
  }

  private replaceCartLines(lines: CardOrderCartLine[]): void {
    this.cartLines = lines;
    this.rebuildCartState();
    this.syncQuantityFromCart();
    this.scheduleDraftPersist();
  }

  private rebuildCartState(): void {
    const counts = new Map<string, number>();
    for (const line of this.cartLines) {
      const key = this.emailKey(line.employee.email);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    this.employeeCartCounts = counts;
    this.cartEmployeeGroups = groupCartLinesByEmployeeOrdered(
      this.cartLines,
      this.selectedEmployees,
    );
    this.employeesWithCartCount = this.selectedEmployees.filter(
      (employee) => (counts.get(this.emailKey(employee.email)) ?? 0) > 0,
    ).length;
    this.summaryRecipients = this.cartEmployeeGroups.map((group) => ({
      employeeEmail: group.employee.email,
      employeeName: group.employee.fullName ?? null,
      items: group.lines.map((line) => {
        const template = this.templates.find(
          (item) => item.printCardTemplateId === line.templateId,
        );
        const preset = this.presets.find(
          (item) => item.id === line.pageSizePresetId,
        );
        return {
          templateId: line.templateId,
          templateName: line.templateName,
          templateType: template?.type ?? "",
          pageSizePresetId: line.pageSizePresetId,
          pageSizeLabelKey: preset?.labelKey,
          orientation:
            template?.settings?.orientation === "portrait" ||
            template?.settings?.designDocument?.orientation === "portrait"
              ? "portrait"
              : "landscape",
          quantity: line.quantity,
        };
      }),
    }));
  }

  removeCartLine(lineId: string, notify = true): void {
    this.replaceCartLines(this.cartLines.filter((line) => line.id !== lineId));
    if (notify) {
      this.toastr.success("cardOrders.removedFromCart");
    }
  }

  removeEmployeeCartLines(email: string): void {
    const nextLines = this.cartLines.filter(
      (line) => line.employee.email !== email,
    );
    if (nextLines.length === this.cartLines.length) {
      return;
    }
    this.replaceCartLines(nextLines);
    this.toastr.success("cardOrders.removedEmployeeFromCart");
  }

  private refreshAccessPointsWhenAddressReady(): void {
    if (this.checkoutDeliveryType !== "ACCESS_POINT") {
      return;
    }
    const shipping = this.checkoutShipping;
    if (
      !shipping.line1?.trim() ||
      !shipping.city?.trim() ||
      !shipping.postalCode?.trim() ||
      !shipping.country?.trim()
    ) {
      return;
    }
    this.refreshAccessPoints();
  }

  private switchBranchDeliveryToCustomWithAddress(): boolean {
    if (this.isCustomDeliveryView || !this.selectedBranchShipping) {
      return false;
    }

    const branchShipping = this.selectedBranchShipping;
    this.deliveryDestination = "custom";
    this.checkoutForm.patchValue(
      {
        line1: branchShipping.address.line1,
        line2: branchShipping.address.line2 ?? "",
        city: branchShipping.address.city,
        postalCode: branchShipping.address.postalCode,
        country: branchShipping.address.country,
        ...(branchShipping.phone ? { phone: branchShipping.phone } : {}),
      },
      { emitEvent: false },
    );
    this.onCheckoutDeliveryChanged();
    return true;
  }

  private refreshAccessPoints(): void {
    if (this.checkoutDeliveryType !== "ACCESS_POINT") {
      return;
    }
    const shipping = this.checkoutShipping;
    const requestSeq = ++this.accessPointsRequestSeq;
    this.accessPointsLoading = true;
    this.upsService
      .searchAccessPoints({
        line1: shipping.line1,
        city: shipping.city,
        postalCode: shipping.postalCode,
        country: shipping.country,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (points) => {
          if (requestSeq !== this.accessPointsRequestSeq) {
            return;
          }
          this.accessPoints = points;
          this.accessPointsLoading = false;
          const selectedId = shipping.accessPointId;
          const hasSelection = points.some((point) => point.id === selectedId);
          if (points.length === 1) {
            this.checkoutForm.patchValue(
              { accessPointId: points[0].id },
              { emitEvent: false },
            );
            this.updateCanSubmitOrder();
            return;
          }
          if (!hasSelection) {
            this.checkoutForm.patchValue(
              { accessPointId: "" },
              { emitEvent: false },
            );
          }
          this.updateCanSubmitOrder();
        },
        error: (error) => {
          if (requestSeq !== this.accessPointsRequestSeq) {
            return;
          }
          this.accessPointsLoading = false;
          this.toastr.apiError(
            error,
            "cardOrders.shippingAddressErrors.accessPointsSearchFailed",
          );
        },
      });
  }

  submitOrder(): void {
    if (this.submitting) {
      return;
    }
    if (!this.validateCheckoutForOrder()) {
      return;
    }

    const shipping = this.checkoutShipping;
    this.submitting = true;

    const completeOrder = (order: CardOrder) => {
      this.submitting = false;
      this.addressValidationState = "idle";
      clearCardOrderCreateDraft();
      this.toastr.success(
        order.status === "PENDING_APPROVAL"
          ? "cardOrders.createPendingApprovalSuccess"
          : "cardOrders.createSuccess",
      );
      const orderId = (order.printCardOrderId || "").trim();
      if (!orderId) {
        void this.router.navigate(["/card-orders"]);
        return;
      }
      void this.router.navigate(["/card-orders", orderId]);
    };

    const failOrder = (error: unknown, fallbackKey: string) => {
      this.submitting = false;
      this.toastr.apiError(error, fallbackKey);
    };

    if (!this.orderOptions?.upsEnabled) {
      this.placeOrderRequest()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: completeOrder,
          error: () => failOrder(null, "cardOrders.createFailed"),
        });
      return;
    }

    const placeOrder$ =
      shipping.deliveryType === "ACCESS_POINT"
        ? this.upsService
            .searchAccessPoints({
              line1: shipping.line1,
              city: shipping.city,
              postalCode: shipping.postalCode,
              country: shipping.country,
            })
            .pipe(
              switchMap((points) => {
                this.accessPoints = points;
                const selectedPoint = points.find(
                  (point) => point.id === shipping.accessPointId,
                );
                if (!selectedPoint) {
                  if (!this.isCustomDeliveryView) {
                    this.switchBranchDeliveryToCustomWithAddress();
                  }
                  failOrder(
                    null,
                    "cardOrders.shippingAddressErrors.accessPointUnavailable",
                  );
                  return EMPTY;
                }
                return this.placeOrderRequest();
              }),
            )
        : this.placeOrderRequest();

    placeOrder$
      .pipe(
        catchError((error) => {
          if (!this.isCustomDeliveryView) {
            this.switchBranchDeliveryToCustomWithAddress();
          }
          failOrder(error, "cardOrders.createFailed");
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: completeOrder,
        error: (error) => failOrder(error, "cardOrders.createFailed"),
      });
  }

  private placeOrderRequest() {
    const shipping = this.checkoutShipping;
    const employeeEmail =
      this.isSelfServiceOrder && this.activeEmployee
        ? this.activeEmployee.email
        : (this.auth.appUser?.email ?? "");
    const recipients = this.isSelfServiceOrder
      ? cartLinesToSelfServiceRecipientsPayload(this.cartLines, employeeEmail)
      : cartLinesToRecipientsPayload(this.cartLines);
    const notes = shipping.notes?.trim() || undefined;
    const companyName = shipping.companyName?.trim() || undefined;
    return this.ordersService.createOrder({
      recipients,
      ...(shipping.deliveryType === "PICKUP"
        ? {}
        : {
            shippingAddress: {
              line1: shipping.line1,
              line2: shipping.line2 || undefined,
              city: shipping.city,
              postalCode: shipping.postalCode,
              country: shipping.country,
            },
          }),
      phone: shipping.phone,
      recipientName: shipping.recipientName,
      ...(companyName ? { companyName } : {}),
      deliveryType: shipping.deliveryType,
      deliverySpeed: shipping.deliverySpeed,
      ...(this.auth.isSuperAdmin && this.showAdminProductionTime
        ? { productionTime: shipping.productionTime }
        : {}),
      ...(notes ? { notes } : {}),
      upsAccessPoint:
        shipping.deliveryType === "ACCESS_POINT"
          ? (this.selectedAccessPoint ?? undefined)
          : undefined,
    });
  }

  private defaultPageSizePresetId(type: PrintCardTemplateType): string {
    return type === "PLASTIC_NFC"
      ? DEFAULT_PRINT_CARD_PLASTIC_PAGE_SIZE_PRESET_ID
      : PRINT_CARD_DEFAULT_PAGE_SIZE_PRESET_ID;
  }

  readonly getShipmentMethodLabelKey = getShipmentMethodLabelKey;
  readonly formatShippingAddressLines = formatShippingAddressLines;
  readonly formatUpsAccessPointName = formatUpsAccessPointName;
  readonly formatUpsAccessPointAddress = formatUpsAccessPointAddress;
  readonly formatUpsAccessPointDistanceKm = formatUpsAccessPointDistanceKm;

  formatUpsAccessPointOpeningHours(
    value: string | null | undefined,
  ): string | null {
    return localizeUpsAccessPointOpeningHours(
      value,
      (key) => this.translate.instant(key) as string,
    );
  }

  proceedToSummary(): void {
    if (!this.validateCheckoutForOrder()) {
      return;
    }
    this.setStepIndex(this.step.Summary);
  }

  @HostListener("document:keydown", ["$event"])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (
      !(event.metaKey || event.ctrlKey) ||
      event.altKey ||
      event.shiftKey ||
      !this.auth.isSuperAdmin ||
      this.dialog.openDialogs.length > 0
    ) {
      return;
    }
    if (event.code === "KeyP") {
      if (this.stepIndex !== this.step.Summary || !this.upsEnabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.openUpsShopRatesDialog();
      return;
    }
    if (event.code === "KeyO") {
      if (this.stepIndex !== this.step.Checkout || !this.printerEnabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.showAdminProductionTime = !this.showAdminProductionTime;
      if (this.showAdminProductionTime && !this.productionTimeManuallySet) {
        const speed = this.checkoutForm.get("deliverySpeed")
          ?.value as CardOrderDeliverySpeed;
        this.checkoutForm.patchValue(
          {
            productionTime: speed === "EXPRESS" ? "EXPRESS" : "STANDARD",
          },
          { emitEvent: false },
        );
      }
    }
  }

  openUpsShopRatesDialog(): void {
    if (
      !this.auth.isSuperAdmin ||
      !this.upsEnabled ||
      this.stepIndex !== this.step.Summary
    ) {
      return;
    }

    const shipping = this.checkoutShipping;
    const deliveryType = shipping.deliveryType;
    if (deliveryType !== "COURIER" && deliveryType !== "ACCESS_POINT") {
      this.toastr.error("printCardOrders.shopRates.upsDeliveryRequired");
      return;
    }

    if (!this.validateCheckoutForOrder()) {
      return;
    }

    const accessPoint = this.selectedAccessPoint;
    if (deliveryType === "ACCESS_POINT" && !accessPoint) {
      this.toastr.error("cardOrders.shippingAddressErrors.accessPointRequired");
      return;
    }

    const totalWeightKg = this.estimateShopRatesWeightKg();

    this.dialog.open(UpsShopRatesDialogComponent, {
      width: "920px",
      maxWidth: "96vw",
      panelClass: "app-dialog-panel",
      autoFocus: "dialog",
      data: {
        totalWeightKg,
        weightUnit: "KGS",
        fetchRates: (packageWeight: number, weightUnit: UpsWeightUnit) =>
          this.upsService.getShopRates({
            deliveryType,
            recipientName: shipping.recipientName.trim(),
            phone: shipping.phone.trim(),
            ...(shipping.companyName?.trim()
              ? { companyName: shipping.companyName.trim() }
              : {}),
            shippingAddress: {
              line1: shipping.line1.trim(),
              ...(shipping.line2?.trim()
                ? { line2: shipping.line2.trim() }
                : {}),
              city: shipping.city.trim(),
              postalCode: shipping.postalCode.trim(),
              country: shipping.country.trim(),
            },
            ...(deliveryType === "ACCESS_POINT" && accessPoint
              ? {
                  accessPoint: {
                    id: accessPoint.id,
                    name: accessPoint.name,
                    line1: accessPoint.line1,
                    city: accessPoint.city,
                    postalCode: accessPoint.postalCode,
                    country: accessPoint.country,
                  },
                }
              : {}),
            packageWeight,
            weightUnit,
          }),
      },
    });
  }

  private estimateShopRatesWeightKg(): number {
    const cardCount = getTotalCartQuantity(this.cartLines);
    const estimated = Math.round((0.2 + cardCount * 0.05) * 100) / 100;
    return Math.max(0.5, estimated);
  }

  private validateCheckoutForOrder(): boolean {
    this.errorStateMatcher.submitAttempted = true;
    if (this.cartLines.length === 0) {
      this.toastr.error("cardOrders.emptyCart");
      return false;
    }
    const shipping = this.checkoutShipping;
    if (shipping.deliveryType !== "PICKUP") {
      if (!this.isCustomDeliveryView && !this.selectedBranchShipping) {
        this.syncSelectedDeliveryBranch();
      }
      if (!this.isCustomDeliveryView && !this.selectedBranchShipping) {
        this.toastr.error("cardOrders.validationBranchAddress");
        return false;
      }
      if (this.isCustomDeliveryView && !this.checkoutForm.valid) {
        this.toastr.error("cardOrders.validationAddress");
        return false;
      }
    }
    if (!shipping.recipientName?.trim()) {
      this.toastr.error("cardOrders.validationRecipientName");
      return false;
    }
    if (!shipping.phone?.trim() || this.checkoutForm.get("phone")?.invalid) {
      this.toastr.error("cardOrders.validationPhone");
      return false;
    }
    if (!shipping.deliverySpeed) {
      this.toastr.error("cardOrders.validationDeliverySpeed");
      return false;
    }
    if (shipping.deliveryType === "ACCESS_POINT" && !shipping.accessPointId) {
      this.toastr.error("cardOrders.shippingAddressErrors.accessPointRequired");
      return false;
    }
    if (this.upsEnabled && shipping.deliveryType !== "PICKUP") {
      const validation = this.validateAddressWithUps();
      if (!validation.ok) {
        this.toastr.error(
          validation.errorKey ?? "cardOrders.shippingAddressErrors.invalid",
        );
        return false;
      }
    }
    return true;
  }

  getPresetLabel(presetId: string): string {
    const preset = this.presets.find((item) => item.id === presetId);
    return preset ? preset.labelKey : presetId;
  }

  cartLineTypeLabelKey(line: CardOrderCartLine): string | null {
    const template = this.templates.find(
      (item) => item.printCardTemplateId === line.templateId,
    );
    return template ? getTemplateTypeLabelKey(template.type) : null;
  }

  getEmployeeLabel(employee: EmployeePickerItem): string {
    return employee.fullName?.trim() || employee.email;
  }

  cancel(): void {
    void this.router.navigate(["/card-orders"]);
  }
}
